<template>
  <div>
    <Facets
      v-model="facetsValues"
      class="hidden md:block md:w-[250px]"
      :facets
      :format-title-rule="formatFaceTitleRules"
      @input="handleFilter"
    />

    <RevDrawer
      :close-button-label="i18n(modalTranslations.close)"
      :name="modalName"
      :title="i18n(translations.filter)"
    >
      <template #body>
        <Facets
          v-model="facetsValues"
          class="mt-6 w-full"
          :facets
          :format-title-rule="formatFaceTitleRules"
          @input="handleFilter"
        />
      </template>
      <template #footer>
        <RevButton
          full-width="always"
          variant="primary"
          @click="handleFilterCloseModal"
        >
          {{ i18n(translations.applyFilters) }}
        </RevButton>
      </template>
    </RevDrawer>

    <RevStickyBar class="md:hidden">
      <RevButton
        full-width="always"
        variant="primary"
        @click="handleFilterOpenModal"
      >
        {{ i18n(translations.filter) }}
      </RevButton>
    </RevStickyBar>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { LocationQuery } from 'vue-router'

import type { FacetsType } from '@backmarket/http-api/src/api-specs-buyback/seller/buyback-products.types'
import modalTranslations from '@backmarket/nuxt-layer-buyback/utils/Modal.translations'
import MODAL_NAMES from '@backmarket/nuxt-layer-buyback/utils/modals'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { RevDrawer } from '@ds/components/Drawer'
import { closeModal, openModal } from '@ds/components/ModalBase'
import { RevStickyBar } from '@ds/components/StickyBar'

import { FACET_NAME_PREFIX } from '~/scopes/algolia/components/Facets/Facets.constants'
import Facets from '~/scopes/algolia/components/Facets/Facets.vue'

import translations from './SearchFilters.translations'

const props = withDefaults(
  defineProps<{
    facets: FacetsType
    facetFilters: LocationQuery
  }>(),
  {
    facets: () => ({}),
    facetFilters: () => ({}),
  },
)

const emit = defineEmits(['filter'])

const i18n = useI18n()

const formatFaceTitleRules = ref({
  separator: '.',
})

const facetsValues = ref({})

const modalName = computed(() => MODAL_NAMES.REFURBISHER_FILTER_PRODUCTS)

watch(
  () => props.facetFilters,
  (preselectedFilters) => {
    facetsValues.value = Object.entries(preselectedFilters).reduce(
      (acc, [key, value]) => {
        const formattedKey = `${FACET_NAME_PREFIX}${key}`

        return {
          ...acc,
          [formattedKey]: value,
        }
      },
      {},
    )
  },
  { immediate: true },
)

function handleFilterOpenModal() {
  openModal(modalName.value)
}

function handleFilterCloseModal() {
  closeModal(modalName.value)
}

function formatFacetsValues(values: Record<string, unknown>) {
  return Object.entries(values).reduce((acc, [key, value]) => {
    const formattedKey = key.split(FACET_NAME_PREFIX).pop()

    return {
      ...acc,
      [formattedKey as string]: value,
    }
  }, {})
}

function handleFilter(filters: LocationQuery) {
  const formattedFilters = formatFacetsValues(filters)
  emit('filter', formattedFilters)
}
</script>
